"use client"

import { VideoCategoryGridSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/VideoCategorGridySkeleton"
import { Button } from "@/components/ui/button"
import { Images } from "@/constants/images"
import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaVideoCategoryGridLayoutKey } from "@/lib/extract-batch-requests"
import { formatTimeAgo } from "@/lib/utils"
import { VideoData } from "@/types/types"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useRef, useState } from "react"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { twMerge } from "tailwind-merge"
import { VideoCategoryGridLayoutProps } from "."
import VideoModal from "../../../components/VideoModal"
import { generateResponsiveCSS } from "../../../lib/helper"
import { getYouTubeThumbnail } from "../../../lib/youtube-helpers"
import { useStableComponentId } from "../TamdaMediaNewsLayout/hooks/useStableComponentId"

export const RenderConfig = {
  render: ({ category, id: componentId }: VideoCategoryGridLayoutProps & { id?: string }) => {
    const id = useStableComponentId(componentId)
    const responsiveCSS = generateResponsiveCSS(id, {}, "desktop")
    const batchData = useBatchDataMap()
    const videoEntry = resolveBatchDataEntry<{ data?: VideoData[]; meta?: { headerTitle?: string; slugCategory?: string } }>(
      batchData,
      {
        key: buildTamdaMediaVideoCategoryGridLayoutKey(id),
        prefix: "tamda-media-video-category-grid-layout:",
      },
    )
    const videos = Array.isArray(videoEntry?.data) ? videoEntry.data : []
    const loading = !!category && videos.length === 0
    const headerTitle = videoEntry?.meta?.headerTitle || (typeof category === "string" ? "Video" : category?.categoryName) || "Video"
    const slugCategory = videoEntry?.meta?.slugCategory || (typeof category === "string" ? "" : category?.slug) || ""
    const [modalVideo, setModalVideo] = useState<string>("")
    const [isModalOpen, setIsModalOpen] = useState(false)
    const swiperRef = useRef<any>(null)

    const openModal = (youtubeUrl: string) => {
      setModalVideo(youtubeUrl)
      setIsModalOpen(true)
    }

    const closeModal = () => {
      setIsModalOpen(false)
      setModalVideo("")
    }

    const renderVideoCard = (item: VideoData) => {
      const title = item.attributes?.title
      const youtubeUrl = item.attributes?.youtubeUrl || (item as any)?.attributes?.url || ""
      const thumb = getYouTubeThumbnail(youtubeUrl, "hqdefault")
      const videoPublishedAt = item.attributes?.videoPublishedAt
      const videoPublishedAtText = formatTimeAgo(videoPublishedAt)
      return (
        <div className="flex flex-col image-hover cursor-pointer" onClick={() => openModal(youtubeUrl)}>
          <div className="relative w-full group">
            <div className="w-full sm:h-[182px] h-[257px] overflow-hidden z-[1] relative">
              <Image
                src={thumb}
                alt={title}
                fill
                className="object-cover group-hover:scale-110 transition-all duration-300"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center z-[2]">
              <div className="w-[60px] h-[60px] relative">
                <Image src={Images.playIconWhite} alt="play" fill className="object-cover" sizes="60px" />
              </div>
            </div>
          </div>
          <div className="mt-[15px] flex flex-col sm:gap-2.5">
            <h3 className="text-sm sm:text-base leading-[21.7px] sm:leading-[24.8px] text-[#333] sm:font-semibold font-medium hover:text-[#1EAD46] transition-all duration-300">
              {title}
            </h3>
            <p className="sm:text-xs sm:leading-[18.6px] text-[10px] leading-[15.5px] text-[#333] font-normal opacity-50">
              {videoPublishedAtText}
            </p>
          </div>
        </div>
      )
    }

    return (
      <>
        {responsiveCSS}
        <div id={id} className={`${twMerge(id)} container mx-auto max-w-[1160px] px-4`}>
          <div className="flex flex-col sm:gap-[18px] gap-2">
            <div className="bg-[#F5F5F5] flex items-center gap-[18px] h-11 px-2.5">
              <div className="w-[56px] h-[20px]">
                <Image
                  src={Images.subLogo}
                  alt="sub-logo"
                  width={56}
                  height={20}
                  className="w-full h-full object-cover"
                />
              </div>
              <Link
                href={`/category/${slugCategory}`}
                className="sm:text-lg sm:leading-[27.9px] text-base leading-[24.8px] sm:font-bold font-semibold text-[#25262b] hover:text-[#1EAD46]"
              >
                {headerTitle}
              </Link>
            </div>
            {loading ? (
              <VideoCategoryGridSkeleton />
            ) : videos.length === 0 ? (
              <div className="text-center py-20 text-gray-500">No videos found.</div>
            ) : (
              <div className="relative">
                <Swiper
                  slidesPerView={1}
                  onSwiper={instance => (swiperRef.current = instance)}
                  breakpoints={{
                    640: {
                      slidesPerView: 2,
                      spaceBetween: 16,
                    },
                    1024: {
                      slidesPerView: 4,
                      spaceBetween: 10,
                    },
                  }}
                  className="video-category-grid-swiper w-full"
                >
                  {videos.map(video => (
                    <SwiperSlide key={video.id}>{renderVideoCard(video)}</SwiperSlide>
                  ))}
                </Swiper>
                <Button
                  onClick={() => swiperRef.current?.slidePrev()}
                  className="hidden sm:flex items-center justify-center w-[38px] h-[38px] p-0 rounded-full bg-black opacity-40 text-white absolute top-1/2 -translate-y-1/2 left-2 z-[2] hover:bg-black hover:opacity-80"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  onClick={() => swiperRef.current?.slideNext()}
                  className="hidden sm:flex items-center justify-center w-[38px] h-[38px] p-0 rounded-full bg-black opacity-40 text-white absolute top-1/2 -translate-y-1/2 right-2 z-[2] hover:bg-black hover:opacity-80"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <VideoModal isOpen={isModalOpen} videoUrl={modalVideo} onClose={closeModal} />
      </>
    )
  },
}
